import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Enhanced voice recorder hook with MediaRecorder API for mobile support
 * Works on iOS Safari, Android Chrome, and desktop browsers
 */
export function useVoiceRecorder(language = 'en') {
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  
  // Speech recognition for real-time transcription
  const recognitionRef = useRef(null);
  const [transcript, setTranscript] = useState('');
  
  // Cleanup function
  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors when stopping
      }
      recognitionRef.current = null;
    }
    mediaRecorderRef.current = null;
    analyserRef.current = null;
    chunksRef.current = [];
    setAudioLevel(0);
    setRecordingTime(0);
  }, []);
  
  // Analyze audio levels for waveform visualization
  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current) return;
    
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);
    
    // Calculate average level
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const normalizedLevel = Math.min(average / 128, 1); // Normalize to 0-1
    setAudioLevel(normalizedLevel);
    
    if (isRecording) {
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);
    }
  }, [isRecording]);
  
  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript('');
      chunksRef.current = [];
      
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      });
      
      streamRef.current = stream;
      
      // Set up audio analysis for waveform
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      
      // Set up MediaRecorder with appropriate MIME type
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(100); // Collect data every 100ms
      
      // Start speech recognition for real-time transcription (if available)
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        
        // Map language codes
        const langMap = {
          'en': 'en-US',
          'de': 'de-DE',
          'es': 'es-ES',
          'it': 'it-IT',
          'fr': 'fr-FR',
          'ja': 'ja-JP',
        };
        recognition.lang = langMap[language] || 'en-US';
        
        recognition.onresult = (event) => {
          let finalTranscript = '';
          let interimTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalTranscript += result[0].transcript;
            } else {
              interimTranscript += result[0].transcript;
            }
          }
          
          setTranscript(finalTranscript || interimTranscript);
        };
        
        recognition.onerror = (event) => {
          console.warn('Speech recognition error:', event.error);
          // Don't stop recording on speech recognition error
        };
        
        recognitionRef.current = recognition;
        try {
          recognition.start();
        } catch (e) {
          console.warn('Could not start speech recognition:', e);
        }
      }
      
      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      setIsRecording(true);
      
      // Start audio analysis
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);
      
      return true;
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError(err.message || 'Could not access microphone');
      cleanup();
      return false;
    }
  }, [language, analyzeAudio, cleanup]);
  
  // Stop recording and return the recorded audio + transcript
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        cleanup();
        resolve({ audio: null, transcript: transcript });
        return;
      }
      
      // Stop speech recognition first
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore
        }
      }
      
      const finalTranscript = transcript;
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { 
          type: mediaRecorderRef.current?.mimeType || 'audio/webm' 
        });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        setIsRecording(false);
        cleanup();
        
        resolve({ 
          audio: { blob: audioBlob, url: audioUrl }, 
          transcript: finalTranscript 
        });
      };
      
      mediaRecorderRef.current.stop();
    });
  }, [transcript, cleanup]);
  
  // Cancel recording
  const cancelRecording = useCallback(() => {
    setIsRecording(false);
    cleanup();
  }, [cleanup]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);
  
  return {
    isRecording,
    audioLevel,
    recordingTime,
    transcript,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}

/**
 * Format seconds to MM:SS
 */
export function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
